import type { LeverageInfo } from '@/features/tokenPairs';

const FALLBACK_LEVERAGE_OPTIONS = [20, 30, 40] as const;
const MIN_DISPLAY_LEVERAGE = 10;
const MAX_REGULAR_LEVERAGE_STEPS = 5000;

function getStepPrecision(step: number): number {
    const stepStr = step.toString();
    const decimalIndex = stepStr.indexOf('.');

    return decimalIndex === -1 ? 0 : stepStr.length - decimalIndex - 1;
}

function roundToStepPrecision(value: number, step: number): number {
    const precision = getStepPrecision(step);

    return Number(value.toFixed(precision));
}

function buildRegularLeverageValues(
    minLeverage: number,
    maxLeverage: number,
    leverageStep: number,
): number[] {
    if (maxLeverage < minLeverage || leverageStep <= 0) {
        return [];
    }

    const estimatedSteps =
        Math.floor((maxLeverage - minLeverage) / leverageStep) + 1;
    const stepCount = Math.min(estimatedSteps, MAX_REGULAR_LEVERAGE_STEPS);

    const values: number[] = [];
    for (let index = 0; index < stepCount; index += 1) {
        const value = roundToStepPrecision(
            minLeverage + index * leverageStep,
            leverageStep,
        );
        if (value <= maxLeverage) {
            values.push(value);
        }
    }

    const roundedMax = roundToStepPrecision(maxLeverage, leverageStep);
    if (values.at(-1) !== roundedMax) {
        values.push(roundedMax);
    }

    return values;
}

function dedupeSorted(values: number[]): number[] {
    return [...new Set(values)].sort((left, right) => left - right);
}

/**
 * Leverage is whole-number only (per Alex 2026-09-04): the ladder offers
 * 1×, 2×, 3× … never 1.5× or 19.9×, even when the pair reports a fractional
 * step or a list with fractional entries. Hyperliquid itself only takes
 * integer leverage, so nothing tradeable is lost by dropping them.
 */
function keepWholeNumbers(values: number[]): number[] {
    return values.filter((value) => Number.isInteger(value));
}

function wholeNumbersInRange(
    minLeverage: number,
    maxLeverage: number,
): number[] {
    const values: number[] = [];
    for (
        let value = Math.max(1, Math.ceil(minLeverage));
        value <= Math.floor(maxLeverage);
        value += 1
    ) {
        values.push(value);
    }

    return values;
}

function buildWholeLeverageValues(
    minLeverage: number,
    maxLeverage: number,
    leverageStep: number,
): number[] {
    const onLadder = keepWholeNumbers(
        buildRegularLeverageValues(minLeverage, maxLeverage, leverageStep),
    );
    if (onLadder.length > 1) {
        return onLadder;
    }

    // The pair's step never lands on whole numbers (e.g. min 1.1 / step 0.25),
    // so filtering left a dead ladder. Every whole number inside the pair's
    // range is still a valid leverage — offer those instead.
    return wholeNumbersInRange(minLeverage, maxLeverage);
}

function getClosestLeverage(values: number[], currentLeverage: number): number {
    return values.reduce((closest, candidate) => {
        const candidateDistance = Math.abs(candidate - currentLeverage);
        const closestDistance = Math.abs(closest - currentLeverage);

        return candidateDistance < closestDistance ? candidate : closest;
    }, values[0]);
}

function getMiddleValue(values: number[]): number {
    return values[Math.floor(values.length / 2)];
}

function buildRepresentativeLeverages(
    values: number[],
    currentLeverage: number,
): number[] {
    if (values.length <= 3) {
        return values;
    }

    const first = values[0];
    const last = values.at(-1) ?? values[values.length - 1];
    const currentOption = values.includes(currentLeverage)
        ? currentLeverage
        : null;
    const middle =
        currentOption !== null &&
        currentOption !== first &&
        currentOption !== last
            ? currentOption
            : getMiddleValue(values);

    return dedupeSorted([first, middle, last]);
}

/**
 * Full ordered set of leverages the backend accepts for a pair — the
 * slider's snap scale, whole numbers only. Unlike getLeverageOptions
 * (three representative chips, legacy), this returns every allowed value.
 * Empty array = market metadata not loaded yet.
 */
export function getLeverageScale(
    leverages: LeverageInfo | null | undefined,
): number[] {
    if (!leverages) return [];
    if (leverages.leverageList) {
        return keepWholeNumbers(dedupeSorted(leverages.leverageList));
    }
    if (
        leverages.minLeverage !== null &&
        leverages.maxLeverage !== null &&
        leverages.leverageStep !== null
    ) {
        return buildWholeLeverageValues(
            leverages.minLeverage,
            leverages.maxLeverage,
            leverages.leverageStep,
        );
    }
    return [];
}

export function getLeverageOptions(
    leverages: LeverageInfo | null | undefined,
    currentLeverage: number,
): number[] {
    const scale = getLeverageScale(leverages);
    if (scale.length === 0) {
        return [...FALLBACK_LEVERAGE_OPTIONS];
    }

    const preferred = scale.filter((value) => value >= MIN_DISPLAY_LEVERAGE);

    return buildRepresentativeLeverages(
        preferred.length > 0 ? preferred : scale,
        currentLeverage,
    );
}

export function getNearestLeverageOption(
    options: number[],
    currentLeverage: number,
): number {
    if (options.length === 0) {
        return currentLeverage;
    }

    return getClosestLeverage(options, currentLeverage);
}

export function formatLeverage(value: number): string {
    return `${value}x`;
}
